import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Header, Loader, Layout, BankingAccounts, InvestingAccounts } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { v4 as uuidv4 } from 'uuid';
import AccountTypeIcon from '../components/AccountTypeIcon';
import AccountTransactionTypes from '../components/AccountTransactionTypes';
import DeleteIcon from '../components/DeleteIcon';
import { Doughnut } from 'react-chartjs-2';
import { ChartData } from 'chart.js';

/* =========================
 * Constants & Small Helpers
 * ========================= */
const MONTH_MAP: Record<string, string> = {
    '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
    '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug',
    '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
};

const safePercent = (base: number, value: number) =>
    base ? Number(((value / base) * 100).toFixed(2)) : 0;

type Row = any; // keep broad to avoid breaking external types

function sumIncomeOutcome(records: Row[]) {
    return records?.reduce(
        (acc: { income: number; outcome: number; invest: number; transfer: number }, record: Row) => {
            const { TransactionTypeId, TransactionAmount, TransactionCategory } = record.Transactions || {};
            const amount = Number(TransactionAmount) || 0;

            if (TransactionTypeId === 'INCOME' || TransactionTypeId === 'WITHDRAW') {
                acc.income += amount;
            } else if ((TransactionTypeId === 'OUTCOME' || TransactionTypeId === 'TRANSFER') && TransactionCategory === 'Invest') {
                acc.invest += amount;
            } else if (TransactionTypeId === 'TRANSFER') {
                acc.transfer += amount;
            } else {
                acc.outcome += amount;
            }
            return acc;
        },
        { income: 0, outcome: 0, invest: 0, transfer: 0 }
    );
}

/** Generic aggregator to build chart rows */
function aggregateBy(
    items: Row[] = [],
    keySelector: (r: Row) => string | undefined,
    colorSelector?: (r: Row, key: string) => string
) {
    const map = new Map<string, { amount: number; color: string }>();
    items.forEach((item) => {
        const key = keySelector(item);
        if (!key) return;
        const amount = Number(item?.Transactions?.TransactionAmount) || 0;
        const color =
            colorSelector?.(item, key) ??
            item?.TransactionCategories?.Color ??
            '#000';

        const prev = map.get(key);
        if (prev) {
            prev.amount += amount;
        } else {
            map.set(key, { amount, color });
        }
    });

    return Array.from(map, ([name, { amount, color }]) => ({ name, amount, color }));
}

type DoughnutDataset = {
    label: string;
    data: number[];
    backgroundColor: string[];
    borderColor: string;
    borderWidth: number;
};

type DoughnutData = ChartData<'doughnut', number[], string>;

// If you have a helper, make sure it returns DoughnutData:
function makeDoughnutData(
    rows: { name: string; amount: number; color: string }[],
    label: string
): DoughnutData {
    return {
        labels: rows.map(r => r.name),
        datasets: [
            {
                label,
                data: rows.map(r => r.amount),
                backgroundColor: rows.map(r => r.color),
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 0.5,
            },
        ],
    };
}

function uniqueStrings(values: unknown[]): string[] {
    return Array.from(
        new Set(
            values
                .filter((v): v is string => typeof v === 'string') // narrow to string
        )
    );
}

/* ==============
 * Component
 * ============== */
const Account = () => {
    const { id } = useParams();
    const today = new Date().toISOString().split('T')[0];

    // Data
    const [account, setAccount] = useState<Record<string, any>>({});
    const [accounts, setAccounts] = useState<any[]>([]);
    const [transactions, setTransactions] = useState<Row[]>([]);
    const [transactionTypes, setTransactionTypes] = useState<any[]>([]);
    const [transactionNames, setTransactionNames] = useState<any[]>([]);
    const [transactionCategories, setTransactionCategories] = useState<any[]>([]);
    const [transactionYearsAndMonths, setTransactionYearsAndMonths] = useState<any[]>([]);

    // UI / form
    const [loader, setLoader] = useState(true);
    const [transactionName, setTransactionName] = useState('');
    const [transactionCategory, setTransactionCategory] = useState('');
    const [transactionTypeId, setTransactionTypeId] = useState('');
    const [transactionAmount, setTransactionAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState(today);
    const [sourceAccount, setSourceAccount] = useState('');
    const [transactionDetails, setTransactionDetails] = useState('');

    // Filters / derived
    const [filteredTransactions, setFilteredTransactions] = useState<Row[]>([]);
    const [filteredCategories, setFilteredCategories] = useState<Row[]>([]);
    const [transactionMonths, setTransactionMonths] = useState<any[]>([]);
    const [selectedYear, setSelectedYear] = useState<string>('');
    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [data, setData] = useState<DoughnutData>({
        labels: [],
        datasets: [
            { label: '', data: [], backgroundColor: [], borderColor: 'rgba(75, 192, 192, 1)', borderWidth: 0.5 },
        ],
    });

    const [filteredTransactionNames, setFilteredTransactionNames] = useState<string[]>([]);
    const [transactionNamesBackUp, setTransactionNamesBackUp] = useState<any[]>([]);

    // Totals
    const [income, setIncome] = useState(0);
    const [outcome, setOutcome] = useState(0);
    const [invest, setInvest] = useState(0);
    const [transfer, setTransfer] = useState(0);

    const {
        getAccountById,
        getAccountTransactions,
        deleteTransaction,
        deleteTransactionName,
        createTransaction,
        getTransactionTypes,
        getUsersAccounts,
        getUsersTransactionNames,
        deleteTransactionCategory,
        getUsersTransactionCategories,
        getTransactionYearsAndMonths,
        getAccountTranasctionsByYear,
        getAccountTranasctionsByMonthOrCategory,
    } = useStateContext();

    /* -----------------------------
     * Sorting & basic derivations
     * ----------------------------- */
    const sortedTransactions = useMemo(() => {
        return [...transactions].sort(
            (a, b) =>
                new Date(b?.Transactions?.TransactionDate).getTime() -
                new Date(a?.Transactions?.TransactionDate).getTime()
        );
    }, [transactions]);

    useEffect(() => {
        // On raw transactions change, show all (sorted)
        setFilteredTransactions(sortedTransactions);
    }, [sortedTransactions]);

    // Keep totals always in sync with the currently *visible* list
    useEffect(() => {
        const { income, outcome, invest, transfer } = sumIncomeOutcome(filteredTransactions || []);
        setIncome(income);
        setOutcome(outcome);
        setInvest(invest);
        setTransfer(transfer);
    }, [filteredTransactions]);

    /* -----------------------------
     * Initial fetch
     * ----------------------------- */
    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                const [
                    accData,
                    transactionTypesData,
                    accountsData,
                    trsData,
                    trnsNames,
                    trsCategories,
                    trsYandM,
                ] = await Promise.all([
                    getAccountById(id, setLoader),
                    getTransactionTypes(),
                    getUsersAccounts(setLoader),
                    getAccountTransactions(id, setLoader),
                    getUsersTransactionNames(),
                    getUsersTransactionCategories(),
                    getTransactionYearsAndMonths(id),
                ]);

                if (!mounted) return;

                setAccount(accData?.account || {});
                setTransactionTypes(transactionTypesData?.transactionTypes || []);
                setAccounts(accountsData?.accounts || []);
                setTransactions(trsData?.transactions || []);
                setTransactionNames(trnsNames?.transactionNames || []);
                setTransactionNamesBackUp(trnsNames?.transactionNames || []);
                setTransactionCategories(trsCategories?.transactionCategories || []);
                setTransactionYearsAndMonths(trsYandM?.allTransactionYearsAndMonths || []);
            } catch (err) {
                console.error('Error fetching account:', err);
            }
        })();

        return () => {
            mounted = false;
        };
        // Intentionally depend on id & loader as in original
    }, [id, loader]);

    /* -----------------------------
     * Actions
     * ----------------------------- */
    const handleCreateTransaction = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();

            if (!transactionName) return toast('Transaction name can not be empty!');
            if (!transactionTypeId) return toast('Please select transaction type!');
            if (!transactionDate) return toast('Please pick transaction date!');
            if (transactionAmount === '' || Number(transactionAmount) === 0)
                return toast('Please provide transaction amount!');
            if (!transactionCategory) return toast('Please provide transaction category!');
            if (account.RecordTypeName === 'INVESTMENT' && sourceAccount === '')
                return toast('Please select source account!');

            createTransaction(
                {
                    Id: uuidv4(),
                    TransactionName: transactionName,
                    AccountId: id,
                    TransactionCategory: transactionCategory,
                    TransactionAmount: String(transactionAmount).replace(',', '.'),
                    TransactionTypeId: transactionTypeId,
                    SourceAccountId: sourceAccount || account.Id,
                    TransactionDate: transactionDate,
                    TransactionDetails: transactionDetails,
                },
                setLoader
            );

            setTransactionAmount('');
            setTransactionDetails('');
        },
        [
            transactionName,
            transactionTypeId,
            transactionDate,
            transactionAmount,
            transactionCategory,
            account.RecordTypeName,
            sourceAccount,
            account.Id,
            id,
            transactionDetails,
            createTransaction
        ]
    );

    const handleDeleteTransaction = useCallback(
        (transactionId: string) => deleteTransaction(transactionId, setLoader),
        [deleteTransaction]
    );

    const handleCreatTransactionName = useCallback((accountId: string, name: string) => {
        setTransactionName('Transfer to: ' + name);
        setSourceAccount(accountId);
    }, []);

    const handleDeleteTransactionName = useCallback(
        (nameId: string, name: string) => {
            const ok = window.confirm(`Delete transaction ${name}`);
            if (ok) deleteTransactionName(nameId, setLoader);
        },
        [deleteTransactionName]
    );

    const handleDeleteTransactionCategory = useCallback(
        (catId: string, name: string) => {
            const ok = window.confirm(`Delete category ${name}`);
            if (ok) deleteTransactionCategory(catId, setLoader);
        },
        [deleteTransactionCategory]
    );

    const handleDeleteTransactionType = useCallback((id: string, name: string) => {
        setTransactionTypeId(id);
        if (name === 'Transfer' || name === 'Invest' || name === 'Withdraw') {
            setTransactionCategory(name);
        }
    }, []);

    /* -----------------------------
     * Filtering (Year / Month / Category)
     * ----------------------------- */

    const buildAndSetCategoryChart = useCallback((items: Row[], label: string) => {
        const rows = aggregateBy(
            items,
            (it) => it?.Transactions?.TransactionCategory,
            (it) => it?.TransactionCategories?.Color || '#000'
        );
        setData(makeDoughnutData(rows, label));
    }, []);

    const buildAndSetNameChart = useCallback((items: Row[], label: string) => {
        const rows = aggregateBy(
            items,
            (it) => it?.Transactions?.TransactionName,
            () => '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')
        );
        setData(makeDoughnutData(rows, label));
    }, []);

    const handleGetTransactionsByYear = useCallback(
        async (e: any, accountId: string, year: string) => {
            if (selectedYear === year) {
                e.target.checked = false;
                setSelectedYear('');
                setSelectedMonth('');
                setSelectedCategory('');
                setTransactionNames([]);
                setFilteredTransactions(sortedTransactions);
                setFilteredCategories(sortedTransactions);
                setData({ labels: [], datasets: [] });
                return;
            }

            e.target.checked = true;
            setSelectedYear(year);
            setSelectedMonth('');
            setSelectedCategory('');
            const result = await getAccountTranasctionsByYear(accountId, year);
            const list = result?.accountTranasctionsByYear || [];
            setFilteredTransactions(list);
            setFilteredCategories(list);
            setTransactionMonths(transactionYearsAndMonths.filter((i) => i.Y === year));
            buildAndSetCategoryChart(list, 'Spent ' + year);
        },
        [
            selectedYear,
            sortedTransactions,
            getAccountTranasctionsByYear,
            transactionYearsAndMonths,
            buildAndSetCategoryChart
        ]
    );

    const handleGetTransactionsByMonth = useCallback(
        async (e: any, accountId: string, year: string, month: string) => {
            if (selectedMonth === month) {
                e.target.checked = false;
                setSelectedMonth('');
                setSelectedCategory('');
                setTransactionNames([]);

                const result = await getAccountTranasctionsByYear(accountId, year);
                const list = result?.accountTranasctionsByYear || [];
                setFilteredTransactions(list);
                setFilteredCategories(list);
                buildAndSetCategoryChart(list, 'Spent ' + MONTH_MAP[month]);
                return;
            }

            e.target.checked = true;
            setSelectedYear(year);
            setSelectedMonth(month);
            setSelectedCategory('');
            const result = await getAccountTranasctionsByMonthOrCategory(accountId, year, month);
            const list = result?.getAccountTranasctionsByMonthOrCategory || [];
            setFilteredTransactions(list);
            setFilteredCategories(list);
            buildAndSetCategoryChart(list, 'Spent ' + MONTH_MAP[month]);
        },
        [
            selectedMonth,
            getAccountTranasctionsByYear,
            getAccountTranasctionsByMonthOrCategory,
            buildAndSetCategoryChart
        ]
    );

    const handleGetTransactionsByCategory = useCallback(
        async (e: any, accountId: string, year: string, month: string, category: string) => {
            if (selectedCategory === category) {
                e.target.checked = false;
                setSelectedCategory('');
                const result = await getAccountTranasctionsByMonthOrCategory(accountId, year, month);
                const list = result?.getAccountTranasctionsByMonthOrCategory || [];
                setFilteredTransactions(list);
                setFilteredCategories(list);
                buildAndSetCategoryChart(list, 'Spent ' + MONTH_MAP[month]);
                setFilteredTransactionNames([]);
                return;
            }

            e.target.checked = true;
            setSelectedCategory(category);
            const result = await getAccountTranasctionsByMonthOrCategory(accountId, year, month);
            const all = result?.getAccountTranasctionsByMonthOrCategory || [];
            const byCategory = all.filter(
                (it: Row) => it?.Transactions?.TransactionCategory === category
            );
            setFilteredTransactions(byCategory);
            setFilteredCategories(all);

            const names = uniqueStrings(
                byCategory.map(
                    (e: { Transactions?: { TransactionName?: unknown } }) =>
                        e?.Transactions?.TransactionName
                )
            );
            setFilteredTransactionNames(names);

            buildAndSetNameChart(byCategory, 'Spent ' + MONTH_MAP[month]);
        },
        [
            selectedCategory,
            getAccountTranasctionsByMonthOrCategory,
            buildAndSetCategoryChart,
            buildAndSetNameChart
        ]
    );

    /* -----------------------------
     * Form helpers
     * ----------------------------- */
    const handleTransactionCategory = useCallback(
        (e: any, category: string) => {
            e.target.checked = false;
            if (transactionCategory === category) {
                setTransactionCategory('');
                setTransactionNames(transactionNamesBackUp);
            } else {
                setTransactionCategory(category);
                setTransactionNames(transactionNamesBackUp.filter((t) => t.TransactionCategory === category));
            }
        },
        [transactionCategory, transactionNamesBackUp]
    );

    const handleTransactionName = useCallback((e: any, item: any) => {
        const { Name, TransactionCategory } = item || {};
        e.target.checked = false;

        if (transactionName === Name) {
            setTransactionName('');
            setTransactionCategory('');
        } else {
            setTransactionName(Name);
            setTransactionCategory(TransactionCategory);
        }
    }, [transactionName]);

    /* -----------------------------
     * Render
     * ----------------------------- */
    return (
        <Layout>
            {loader && <Loader />}

            <div className="text-gray-200 bg-secondary-dark-bg h-22 rounded-xl w-3/4 mt-10 p-8 pt-9 m-3 mb-5">
                <div className="flex justify-between items-center self-center">
                    {account ? (
                        <div>
                            <p className="text-3xl text-gray-200 font-semibold">
                                <AccountTypeIcon id={account.RecordTypeName} /> {account.AccountName}
                            </p>
                            <p className="text-md text-gray-400 mt-5">$ {account.CurrentWealth}</p>
                        </div>
                    ) : (
                        <p className=" px-5 py-1 bg-blue-400 rounded-lg text-white">Add an account</p>
                    )}
                </div>
            </div>

            <div className="w-full md:w-3/4 m-auto m-2 md:m-10 mt-10 p-5 md:p-10 bg-secondary-dark-bg rounded-3xl">
                {loader && <Loader />}
                <Header category="App" title="Transactions" />

                <div className="mt-5 w-full md:w-400">
                    <form className="max-w-md mx-auto mb-5">
                        <>
                            {/* Category input + quick picks */}
                            <div className="relative z-0 w-full mb-5 group">
                                <input
                                    onChange={(e) => setTransactionCategory(e.target.value)}
                                    value={transactionCategory}
                                    type="text"
                                    placeholder=" Transaction category"
                                    name="transactionCategory"
                                    className="block py-2.5 px-0 w-full text-sm text-gray-500 bg-transparent border-2 border-gray-300 appearance-none rounded-md p-2"
                                />
                                <ul className="mt-4 flex flex-wrap mb-4 mt-2 relative bg-gray-600/10 items-center p-2 rounded-2xl hover:shadow-lg cursor-pointer">
                                    {transactionCategories?.map((item: any) => (
                                        <li key={item.Id} className="mx-1 my-2">
                                            <label htmlFor={item.Id} className="block relative">
                                                <input
                                                    id={item.Id}
                                                    onChange={(e) => handleTransactionCategory(e, item.Name)}
                                                    type="radio"
                                                    name="transactionCategory"
                                                    className="sr-only peer"
                                                />
                                                <div
                                                    style={{ background: item.Color }}
                                                    className="w-full p-2 cursor-pointer rounded-lg shadow-sm ring-white peer-checked:ring-1 duration-200"
                                                >
                                                    <h3 className="leading-none text-gray-200 font-medium text-sm">
                                                        {item.Name}{' '}
                                                        {transactionCategory === item.Name && (
                                                            <span
                                                                className="text-xs"
                                                                onClick={() => handleDeleteTransactionCategory(item.Id, item.Name)}
                                                            >
                                                                ❌
                                                            </span>
                                                        )}
                                                    </h3>
                                                </div>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Name + description + quick picks */}
                            <div className="relative z-0 w-full mb-5 group">
                                <div className="flex">
                                    <input
                                        onChange={(e) => setTransactionName(e.target.value)}
                                        value={transactionName}
                                        type="text"
                                        placeholder=" Transaction name"
                                        className="block mr-3 py-2.5 px-0 w-1/3 text-sm text-gray-500 bg-transparent border-2 border-gray-300 appearance-none rounded-md p-2"
                                    />
                                    <input
                                        onChange={(e) => setTransactionDetails(e.target.value)}
                                        value={transactionDetails}
                                        type="text"
                                        placeholder=" Transaction description"
                                        className="block py-2.5 px-0 w-2/3 text-sm text-gray-500 bg-transparent border-2 border-gray-300 appearance-none rounded-md p-2"
                                    />
                                </div>
                                <ul className="mt-4 flex flex-wrap mb-4 mt-2 relative bg-gray-600/10 rounded-2xl items-center p-2 hover:shadow-lg cursor-pointer">
                                    {transactionNames?.map((item: any) => (
                                        <li key={item.Id} className="mx-1 my-2">
                                            <label htmlFor={item.Id} className="block relative">
                                                <input
                                                    id={item.Id}
                                                    onChange={(e) => handleTransactionName(e, item)}
                                                    type="radio"
                                                    name="transactionName"
                                                    className="sr-only peer"
                                                />
                                                <div className="w-full p-2 cursor-pointer rounded-lg bg-gray-600 shadow-sm ring-white peer-checked:ring-1 duration-200">
                                                    <h3 className="leading-none text-gray-400 font-medium text-sm">
                                                        {item.Name}{' '}
                                                        {transactionName === item.Name && (
                                                            <span
                                                                className="text-xs"
                                                                onClick={() => handleDeleteTransactionName(item.Id, item.Name)}
                                                            >
                                                                ❌
                                                            </span>
                                                        )}
                                                    </h3>
                                                </div>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>

                        <AccountTransactionTypes
                            recordTypeName={account.RecordTypeName}
                            transactionTypes={transactionTypes}
                            handleTransactionTypeId={handleDeleteTransactionType}
                            accounts={accounts}
                            handleSetSourceAccount={setSourceAccount}
                        />
                        {transactionTypeId === 'TRANSFER' && (
                            <>
                                <BankingAccounts
                                    transactionTypeId={transactionTypeId}
                                    handleTransactionTypeId={setTransactionTypeId}
                                    accounts={accounts}
                                    handleSetDestinationAccountAndTransactionName={handleCreatTransactionName}
                                    selfAccountId={id}
                                />
                                <InvestingAccounts
                                    transactionTypeId={transactionTypeId}
                                    handleTransactionTypeId={setTransactionTypeId}
                                    accounts={accounts}
                                    handleSetDestinationAccountAndTransactionName={handleCreatTransactionName}
                                    selfAccountId={id}
                                />
                            </>)}

                        <div className="mb-3 md:w-1/2 w-2/3">
                            <div className="relative z-0 w-full mb-2 group">
                                <div className="relative max-w-sm">
                                    <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                                        <svg
                                            className="w-4 h-4  text-gray-400"
                                            aria-hidden="true"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                                        </svg>
                                    </div>
                                    <input
                                        onChange={(e) => setTransactionDate(e.target.value)}
                                        value={transactionDate}
                                        aria-label="Date"
                                        type="date"
                                        id="datepicker-autohide"
                                        className="border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5  bg-gray-700 border-gray-600 placeholder-gray-400 text-white"
                                    />
                                </div>
                            </div>
                            <div className="relative z-0 w-full group mb-3">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    onChange={(e) => setTransactionAmount(e.target.value)}
                                    value={transactionAmount}
                                    placeholder=" Transaction amount"
                                    className="block py-2.5 px-0 w-full text-sm text-gray-500 bg-transparent border-2 border-gray-300 appearance-none rounded-md p-2"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleCreateTransaction}
                            className="bg-gray-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-1/2 sm:w-auto px-5 py-2.5 text-center text-gray-300"
                        >
                            Create
                        </button>
                    </form>

                    {/* -------------------- Filters -------------------- */}
                    <div className="p-6 text-center max-w-md mx-auto mb-5 bg-gradient-to-br from-gray-700/40 to-gray-800/30 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-600/30">
                        <h2 className="text-gray-100 font-semibold text-sm mb-4 tracking-wide">Filters</h2>

                        {/* Years */}
                        <p className="text-gray-300 font-semibold text-xs tracking-wide">Years</p>
                        <ul className="flex flex-wrap justify-center gap-2 mt-2">
                            {[...new Set(transactionYearsAndMonths?.map((item: any) => item.Y))]?.map((year: string) => (
                                <li key={year}>
                                    <label htmlFor={year} className="block">
                                        <input
                                            id={year}
                                            onClick={(e) => handleGetTransactionsByYear(e, id as string, year)}
                                            type="radio"
                                            name="filteredYear"
                                            className="sr-only peer"
                                        />
                                        <div className="bg-gray-700/40 px-3 py-1.5 rounded-lg cursor-pointer shadow-sm border border-transparent hover:border-blue-400/50 peer-checked:border-blue-400 peer-checked:bg-blue-500/30 transition-all duration-200">
                                            <h3 className="text-gray-200 font-medium text-xs">{year}</h3>
                                        </div>
                                    </label>
                                </li>
                            ))}
                        </ul>

                        {/* Months */}
                        {selectedYear && (
                            <>
                                <p className="text-gray-300 font-semibold text-xs tracking-wide mt-5">Months</p>
                                <ul className="flex flex-wrap justify-center gap-2 mt-2">
                                    {Object.entries(MONTH_MAP).map(([num, month]) => {
                                        const selectedYearMonths = [...new Set(transactionMonths?.map((i: any) => i.M))];
                                        if (!selectedYearMonths.includes(num)) return null;

                                        return (
                                            <li key={month}>
                                                <label htmlFor={month} className="block">
                                                    <input
                                                        id={month}
                                                        onClick={(e) => handleGetTransactionsByMonth(e, id as string, selectedYear, num)}
                                                        type="radio"
                                                        name="filteredMonth"
                                                        className="sr-only peer"
                                                    />
                                                    <div className="bg-gray-700/40 px-3 py-1.5 rounded-lg cursor-pointer shadow-sm border border-transparent hover:border-green-400/50 peer-checked:border-green-400 peer-checked:bg-green-500/30 transition-all duration-200">
                                                        <h3 className="text-gray-200 font-medium text-xs">{month}</h3>
                                                    </div>
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        )}

                        {/* Categories */}
                        {(selectedYear || selectedMonth) &&
                            [...new Set(filteredCategories?.map((it: Row) => it?.Transactions?.TransactionCategory))].length > 1 && (
                                <>
                                    <p className="text-gray-300 font-semibold text-xs tracking-wide mt-5">Categories</p>
                                    <ul className="flex flex-wrap justify-center gap-2 mt-2">
                                        {[...new Set(filteredCategories?.map((it: Row) => it?.Transactions?.TransactionCategory))]?.map(
                                            (category: string) => (
                                                <li key={category}>
                                                    <label htmlFor={category} className="block">
                                                        <input
                                                            id={category}
                                                            onClick={(e) =>
                                                                handleGetTransactionsByCategory(e, id as string, selectedYear, selectedMonth, category)
                                                            }
                                                            type="radio"
                                                            name="filteredCategory"
                                                            className="sr-only peer"
                                                        />
                                                        <div
                                                            style={{
                                                                background:
                                                                    transactionCategories.find((it: any) => it.Name === category)?.Color || '#4B5563',
                                                            }}
                                                            className="px-3 py-1.5 rounded-lg cursor-pointer shadow-sm border border-transparent hover:brightness-110 peer-checked:ring-2 peer-checked:ring-white transition-all duration-200"
                                                        >
                                                            <h3 className="text-gray-200 font-medium text-xs">{category}</h3>
                                                        </div>
                                                    </label>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </>
                            )}

                        {/* Summary */}
                        {selectedCategory && (
                            <>
                                <p className="text-gray-300 font-semibold text-xs tracking-wide mt-5">Summary</p>
                                <section className="flex justify-center gap-4 mt-5">
                                    <div className="text-xs font-medium text-white">
                                        Spent <sup className="italic">{safePercent(income, outcome)}%</sup>
                                        <br />
                                        <br />
                                        <span className="bg-red-500/40 rounded-md px-3 py-1 text-xs font-medium text-gray-200 shadow-inner">
                                            - {Number(outcome.toFixed(2))}
                                        </span>
                                    </div>
                                </section>
                            </>
                        )}
                        {!selectedCategory && (
                            <>
                                <p className="text-gray-300 font-semibold text-xs tracking-wide mt-5">Summary</p>
                                <section className="flex justify-center gap-4 mt-5">
                                    <div className="text-xs font-medium text-white">
                                        Earned
                                        <br />
                                        <br />
                                        <span className="bg-blue-500/40 rounded-md px-3 py-1 text-xs font-medium text-gray-200 shadow-inner">
                                            + {Number(income.toFixed(2))}
                                        </span>
                                    </div>
                                    <div className="text-xs font-medium text-white">
                                        Spent <sup className="italic">{safePercent(income, outcome)}%</sup>
                                        <br />
                                        <br />
                                        <span className="bg-red-500/40 rounded-md px-3 py-1 text-xs font-medium text-gray-200 shadow-inner">
                                            - {Number(outcome.toFixed(2))}
                                        </span>
                                    </div>
                                </section>
                                <section className="flex justify-center gap-4 mt-5">
                                    <div className="text-xs font-medium text-white">
                                        Invested <sup className="italic">{safePercent(income, invest)}%</sup>
                                        <br />
                                        <br />
                                        <span className="bg-green-500/40 rounded-md px-3 py-1 text-xs font-medium text-gray-200 shadow-inner">
                                            {Number(invest.toFixed(2))}
                                        </span>
                                    </div>
                                    <div className="text-xs font-medium text-white">
                                        Transfered <sup className="italic">{safePercent(income, transfer)}%</sup>
                                        <br />
                                        <br />
                                        <span className="bg-green-200/40 rounded-md px-3 py-1 text-xs font-medium text-gray-200 shadow-inner">
                                            {Number(transfer.toFixed(2))}
                                        </span>
                                    </div>
                                </section>
                            </>
                        )}
                    </div>

                    {/* Chart */}
                    {(
                        (selectedMonth &&
                            [...new Set(filteredCategories?.map((it: Row) => it?.Transactions?.TransactionCategory))].length > 1) ||
                        (selectedYear &&
                            [...new Set(filteredCategories?.map((it: Row) => it?.Transactions?.TransactionCategory))].length > 1) ||
                        (selectedCategory && filteredTransactionNames.length > 1)
                    ) && (
                            <div className="p-6 text-center max-w-md mx-auto mb-5 bg-gradient-to-br from-gray-700/40 to-gray-800/30 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-600/30">
                                <Doughnut data={data} />
                            </div>
                        )}

                    {/* List */}
                    <section className="mb-20 mt-10">
                        {filteredTransactions?.map((item: Row, index: number) => (
                            <div key={index} className="relative bg-gray-600 items-center mt-2 p-2 rounded-2xl hover:shadow-lg cursor-pointer">
                                <div className="flex justify-between w-full">
                                    <div className="flex gap-4">
                                        <section
                                            style={{
                                                backgroundColor: item?.Transactions?.TransactionTypeId === 'INCOME' ? 'lightgreen' : 'salmon',
                                            }}
                                            className="text-2xl rounded-lg p-1 hover:drop-shadow-xl"
                                        >
                                            <AccountTypeIcon id={item?.Accounts?.RecordTypeName} />
                                        </section>

                                        <section>
                                            <div className="text-sm md:text-md text-gray-200 font-semibold">
                                                {item?.Transactions?.TransactionTypeId === 'TRANSFER' ? (
                                                    <div>
                                                        <Link className="underline" to={`/account/${item?.Transactions?.SourceAccountId}`}>
                                                            {item?.Transactions?.TransactionName}
                                                        </Link>{' '}
                                                        ${item?.Transactions?.TransactionAmount}
                                                    </div>
                                                ) : (
                                                    <div className="text-md text-gray-400">
                                                        {item?.Transactions?.TransactionName} ${item?.Transactions?.TransactionAmount}
                                                    </div>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400">
                                                <strong>{item?.Accounts?.AccountName}</strong> ${item?.Transactions?.BalanceAfterTransaction}
                                            </p>
                                            <p className="text-xs text-gray-400">📅 {item?.Transactions?.TransactionDate}</p>
                                        </section>
                                    </div>

                                    <section className="text-xs text-gray-400 w-1/3">
                                        {item?.Transactions?.TransactionDetails}
                                    </section>

                                    <section className="text-right">
                                        {!item?.Transactions?.RelatedTransactionId && (
                                            <button
                                                id={item?.Transactions?.Id}
                                                onClick={() => handleDeleteTransaction(item?.Transactions?.Id)}
                                                className=" top-3 right-3"
                                            >
                                                <DeleteIcon />
                                            </button>
                                        )}
                                        <div>
                                            <span
                                                style={{ background: item?.TransactionCategories?.Color }}
                                                className="inline-flex items-center rounded-md  px-2 py-1 text-xs font-medium text-gray-200 inset-ring inset-ring-gray-400/20"
                                            >
                                                {item?.Transactions?.TransactionCategory}
                                            </span>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        ))}
                    </section>
                </div>
            </div>

            <ToastContainer />
        </Layout>
    );
};

export default Account;